import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Text, BlockStack, Banner } from '@shopify/polaris';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { canDeployStore } from '../utils/subscriptionPlan';
import { buildStorefrontUrl } from '../utils/storeDomain';
import { useAppT } from '../i18n/appT';

export function useStorefrontAccess() {
  const { user, currentStore } = useAuth();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = useAppT(lang);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const canOpenStorefront = useMemo(() => canDeployStore(user), [user]);

  const openStorefront = useCallback((url) => {
    const target = url || buildStorefrontUrl(currentStore);
    if (!target) return;
    if (!canDeployStore(user)) {
      setUpgradeOpen(true);
      return;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  }, [user, currentStore]);

  const closeUpgrade = useCallback(() => setUpgradeOpen(false), []);

  const goUpgrade = useCallback(() => {
    setUpgradeOpen(false);
    navigate('/channels/kiostore?tab=plan');
  }, [navigate]);

  const upgradeModal = (
    <Modal
      open={upgradeOpen}
      onClose={closeUpgrade}
      title={t('store_deploy_upgrade_title')}
      primaryAction={{ content: t('store_deploy_upgrade_cta'), onAction: goUpgrade }}
      secondaryActions={[{ content: t('store_deploy_upgrade_cancel'), onAction: closeUpgrade }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          <Banner tone="info">{t('store_deploy_upgrade_banner')}</Banner>
          <Text as="p">{t('store_deploy_upgrade_body')}</Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );

  return { canOpenStorefront, openStorefront, upgradeModal };
}
